<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { auth } from '../../stores/auth.store';
  import { onMount, tick } from 'svelte';
  import AuthPageLayout from '../../components/auth/AuthPageLayout.svelte';

  let digits = ['', '', '', ''];
  let loading = false;
  let error = '';
  let email: string | null = null;

  onMount(() => {
    email = localStorage.getItem('userEmail');

    if (!email) {
      push('/');
    }
  });

  function applyOtpDigits(raw: string) {
    const otpDigits = raw.replace(/\D/g, '').slice(0, 4);
    if (!otpDigits) return;

    const newDigits = ['', '', '', ''];
    for (let i = 0; i < otpDigits.length; i++) {
      newDigits[i] = otpDigits[i];
    }
    digits = newDigits;
    error = '';

    if (otpDigits.length === 4) {
      tick().then(() => submitOtp());
      return;
    }

    tick().then(() => {
      document.getElementById(`digit-${otpDigits.length}`)?.focus();
    });
  }

  function handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    applyOtpDigits(pasted);
  }

  function handleDigitInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value.length > 1) {
      applyOtpDigits(value);
      return;
    }

    if (value && /^\d$/.test(value)) {
      digits[index] = value;
      digits = [...digits];
      error = '';

      if (index < 3) {
        tick().then(() => {
          const nextInput = document.getElementById(`digit-${index + 1}`);
          nextInput?.focus();
        });
      }
    } else {
      digits[index] = '';
      digits = [...digits];
    }
  }

  function handleKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
        digits[index - 1] = '';
        digits = [...digits];
      }
    }
  }

  async function submitOtp() {
    if (digits.some((digit) => !digit)) {
      error = 'Please enter all 4 digits';
      return;
    }

    loading = true;
    error = '';

    try {
      const otpCode = digits.join('');

      if (!email) {
        error = 'Email not found. Please try again.';
        return;
      }

      const result = await auth.verifyOtpEmail(otpCode, email);

      if (result.success) {
        push('/dashboard');
      } else {
        error = 'Invalid verification code';
      }
    } catch (err) {
      error = 'An error occurred during verification';
      console.error(err);
    } finally {
      loading = false;
    }
  }

  function resendCode() {
    if (!email) return;

    auth
      .resendOtpEmail(email)
      .then(() => {
        error = '';
      })
      .catch((err) => {
        error = 'Failed to resend code';
        console.error(err);
      });
  }

  function wrongEmail() {
    push('/signup');
  }
</script>

<AuthPageLayout title="Verify your email" subtitle="Enter the 4-digit code we sent to your inbox.">
  <p class="auth-subtitle otp-email-line">
    {#if email}
      Sent to <strong>{email}</strong>.
    {/if}
    <a href={null} class="auth-inline-link" on:click|preventDefault={wrongEmail}>Wrong email?</a>
  </p>

  {#if error}
    <div class="auth-form-error" role="alert">{error}</div>
  {/if}

  <section class="auth-form-section" aria-labelledby="otp-code-heading">
    <h2 id="otp-code-heading" class="auth-form-section-title">Verification code</h2>
    <div class="auth-form-section-body">
      <div class="auth-otp-inputs" role="group" aria-label="4-digit verification code">
        {#each digits as digit, index}
          <input
            id="digit-{index}"
            class="auth-otp-digit"
            class:error={!!error}
            type="text"
            inputmode="numeric"
            maxlength="1"
            value={digit}
            disabled={loading}
            aria-label="Digit {index + 1} of 4"
            on:input={(e) => handleDigitInput(e, index)}
            on:paste={handlePaste}
            on:keydown={(e) => handleKeyDown(e, index)}
          />
        {/each}
      </div>
    </div>
  </section>

  <div class="auth-actions">
    <button type="button" class="auth-btn" on:click={submitOtp} disabled={loading}>
      {loading ? 'Verifying...' : 'Verify email'}
    </button>
  </div>

  <svelte:fragment slot="footer">
    Didn't receive the code?
    <a href={null} on:click|preventDefault={resendCode}>Resend code</a>
  </svelte:fragment>
</AuthPageLayout>

<style>
  .otp-email-line {
    margin: -0.5rem 0 1rem;
  }

  .otp-email-line strong {
    color: var(--auth-ink, #1f2937);
    font-weight: 600;
  }
</style>
